import { useEffect, useState } from "react";
import Icon from "../Icon";
import { BtnGhost, BtnPrimary } from "../Atoms";
import {
  ACCIONES, FIJOS, RESERVADAS, porDefecto, nombreTecla, etiquetaTecla, conflictoDe,
} from "./atajos";

// Gestor de atajos. Se abre desde la barra del lienzo.
//
// Para asignar una tecla se PULSA la tecla, no se escribe su nombre: es la
// unica forma de que lo que se guarda sea exactamente lo que el teclado manda,
// sin que nadie tenga que saber como se llama internamente.
export default function AtajosModal({ mapa, onGuardar, onCerrar }) {
  const [local, setLocal] = useState(mapa);
  const [capturando, setCapturando] = useState(null);   // id de accion
  const [aviso, setAviso] = useState(null);

  // Mientras se captura, el teclado es de este modal y de nadie mas.
  useEffect(() => {
    if (!capturando) return;
    const onKey = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === "Escape") { setCapturando(null); setAviso(null); return; }

      const k = nombreTecla(e);
      if (e.ctrlKey || e.metaKey || e.altKey) {
        setAviso("Los atajos del editor son de una sola tecla, sin Ctrl ni Alt.");
        return;
      }
      if (RESERVADAS.has(k)) {
        setAviso(`${etiquetaTecla(k)} ya la usa el editor para otra cosa.`);
        return;
      }
      const choque = conflictoDe(local, capturando, k);
      if (choque) {
        const otra = ACCIONES.find(a => a.id === choque);
        setAviso(`${etiquetaTecla(k)} ya esta en «${otra?.label}». Se la quitas a esa.`);
        // No se bloquea: se le quita a la otra, que es lo que la gente espera.
        setLocal(p => ({ ...p, [choque]: null, [capturando]: k }));
        setCapturando(null);
        return;
      }
      setLocal(p => ({ ...p, [capturando]: k }));
      setCapturando(null);
      setAviso(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturando, local]);

  const fila = (a) => {
    const tecla = local[a.id];
    const activo = capturando === a.id;
    return (
      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 9,
        padding: "7px 2px", borderBottom: "1px solid var(--border2)" }}>
        <Icon name={a.icon} size={13} color="var(--text3)" />
        <span style={{ flex: 1, fontSize: 12.5, color: "var(--text2)" }}>{a.label}</span>

        <button className="btn" onClick={() => { setCapturando(activo ? null : a.id); setAviso(null); }}
          title={activo ? "Pulsa la tecla que quieras" : "Cambiar el atajo"}
          style={{ minWidth: 74, padding: "5px 10px", borderRadius: 7, cursor: "pointer",
            fontFamily: "var(--mono)", fontSize: 11.5, fontWeight: 600,
            border: `1px ${activo ? "dashed" : "solid"} ${activo ? "var(--accent)" : "var(--border)"}`,
            background: activo ? "var(--accent-bg)" : "var(--surface2)",
            color: activo ? "var(--accent)" : tecla ? "var(--text)" : "var(--text3)" }}>
          {activo ? "pulsa…" : tecla ? etiquetaTecla(tecla) : "sin atajo"}
        </button>

        <button aria-label="Quitar el atajo" className="btn" onClick={() => setLocal(p => ({ ...p, [a.id]: null }))}
          disabled={!tecla} title="Quitar el atajo"
          style={{ background: "none", border: "none", padding: 3,
            cursor: tecla ? "pointer" : "default", opacity: tecla ? 1 : .25 }}>
          <Icon name="x-lg" size={10} color="var(--text3)" />
        </button>
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: "var(--text)" }}>
        Atajos del editor de flujo
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 14, lineHeight: 1.55 }}>
        Pulsa un atajo y despues la tecla que quieras. Son de una sola tecla y solo
        funcionan cuando el foco esta en el lienzo, no mientras escribes en el panel.
      </div>

      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: "var(--text3)", marginBottom: 4 }}>
        Insertar
      </div>
      {ACCIONES.filter(a => a.id.startsWith("nodo:")).map(fila)}

      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: "var(--text3)", margin: "13px 0 4px" }}>
        Acciones
      </div>
      {ACCIONES.filter(a => !a.id.startsWith("nodo:")).map(fila)}

      {aviso && (
        <div style={{ marginTop: 11, background: "var(--warn-bg)", border: "1px solid var(--warn-line)",
          color: "var(--warn-text)", borderRadius: 8, padding: "7px 10px", fontSize: 11.5,
          lineHeight: 1.5 }}>
          {aviso}
        </div>
      )}

      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".07em", color: "var(--text3)", margin: "15px 0 5px" }}>
        Fijos
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 4 }}>
        {FIJOS.map(f => (
          <div key={f.teclas} style={{ display: "flex", gap: 9, fontSize: 11.5,
            color: "var(--text3)" }}>
            <span style={{ fontFamily: "var(--mono)", minWidth: 104 }}>{f.teclas}</span>
            <span>{f.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "space-between",
        alignItems: "center", marginTop: 18 }}>
        <BtnGhost onClick={() => { setLocal(porDefecto()); setAviso(null); }}>
          Volver a los de fabrica
        </BtnGhost>
        <div style={{ display: "flex", gap: 8 }}>
          <BtnGhost onClick={onCerrar}>Cancelar</BtnGhost>
          <BtnPrimary onClick={() => { onGuardar(local); onCerrar(); }}>Guardar</BtnPrimary>
        </div>
      </div>
    </div>
  );
}
